import React, { useState, useEffect } from 'react';
import { 
  SparklesIcon, 
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { AIConfidencePreview as AIConfidencePreviewType } from '../types/DocumentTypes';

interface AIConfidencePreviewProps {
  documentFile: File;
  processingProfile: string;
  onConfidenceUpdate: (confidence: AIConfidencePreviewType) => void;
  disabled?: boolean;
}

export const AIConfidencePreview: React.FC<AIConfidencePreviewProps> = ({
  documentFile,
  processingProfile,
  onConfidenceUpdate,
  disabled = false
}) => {
  const [confidencePreview, setConfidencePreview] = useState<AIConfidencePreviewType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate confidence preview when file or profile changes
  useEffect(() => {
    if (documentFile && processingProfile && !disabled) {
      generateConfidencePreview();
    }
  }, [documentFile, processingProfile]);

  const generateConfidencePreview = async () => {
    if (!documentFile || !processingProfile) return;

    setLoading(true);
    setError(null);

    try {
      // TODO: Replace with actual API call to AI service
      // const response = await fetch('/api/v1/dms/ai/confidence-preview', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({
      //     file: documentFile,
      //     processingProfile,
      //     fileSize: documentFile.size,
      //     fileType: documentFile.type
      //   })
      // });
      // const data = await response.json();

      // Mock confidence preview for now
      const mockPreview: AIConfidencePreviewType = generateMockConfidencePreview(documentFile, processingProfile);
      
      setConfidencePreview(mockPreview);
      onConfidenceUpdate(mockPreview);
    } catch (err) {
      setError('Failed to generate confidence preview');
      console.error('AI confidence preview error:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateMockConfidencePreview = (file: File, profile: string): AIConfidencePreviewType => {
    const fileSize = file.size;
    const fileType = file.type;
    const fileName = file.name.toLowerCase();

    // Base confidence based on file type
    let baseConfidence = 0.75;
    if (fileType.includes('pdf')) baseConfidence = 0.85;
    if (fileType.includes('docx') || fileType.includes('doc')) baseConfidence = 0.90;
    if (fileType.includes('txt')) baseConfidence = 0.95;

    // Adjust based on file size (larger files may be more complex)
    if (fileSize > 10 * 1024 * 1024) baseConfidence -= 0.1; // Files > 10MB
    if (fileSize < 1024 * 1024) baseConfidence += 0.05; // Files < 1MB

    // Adjust based on processing profile
    let profileMultiplier = 1.0;
    switch (profile) {
      case 'contract_risk_review':
        profileMultiplier = 0.95; // Slightly lower for complex analysis
        break;
      case 'discovery_redaction':
        profileMultiplier = 0.90; // Lower for redaction tasks
        break;
      case 'pleading_classification':
        profileMultiplier = 0.98; // High confidence for classification
        break;
      default:
        profileMultiplier = 1.0;
    }

    // Generate task-specific confidences
    const taskConfidences = {
      ocr: Math.min(0.95, baseConfidence + (fileName.includes('scan') ? 0.05 : 0)),
      classification: Math.min(0.92, baseConfidence * profileMultiplier),
      citation_extraction: Math.min(0.88, baseConfidence * profileMultiplier),
      risk_assessment: Math.min(0.85, baseConfidence * profileMultiplier * 0.95)
    };

    const overallConfidence = Object.values(taskConfidences).reduce((sum, conf) => sum + conf, 0) / Object.keys(taskConfidences).length;

    // Generate recommendations
    const recommendations: string[] = [];
    if (overallConfidence < 0.8) {
      recommendations.push('Consider manual review for critical sections');
    }
    if (fileSize > 5 * 1024 * 1024) {
      recommendations.push('Large file detected - processing may take longer');
    }
    if (fileType.includes('pdf') && !fileName.includes('text')) {
      recommendations.push('PDF may require OCR processing');
    }
    if (overallConfidence > 0.9) {
      recommendations.push('High confidence - automated processing recommended');
    }

    // Estimate processing time based on file size and profile
    let estimatedTime = Math.max(30, fileSize / (1024 * 1024) * 10); // Base 30s + 10s per MB
    switch (profile) {
      case 'contract_risk_review':
        estimatedTime *= 1.5;
        break;
      case 'discovery_redaction':
        estimatedTime *= 2.0;
        break;
      case 'pleading_classification':
        estimatedTime *= 1.2;
        break;
    }

    return {
      overallConfidence,
      taskConfidences,
      estimatedProcessingTime: Math.round(estimatedTime),
      recommendations
    };
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return 'text-green-600 bg-green-50 border-green-200';
    if (confidence >= 0.8) return 'text-lex-accent bg-lex-accent-soft border-lex-accent';
    if (confidence >= 0.7) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getConfidenceIcon = (confidence: number) => {
    if (confidence >= 0.9) return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
    if (confidence >= 0.8) return <CheckCircleIcon className="h-5 w-5 text-lex-accent" />;
    if (confidence >= 0.7) return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />;
    return <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />;
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  if (disabled) return null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SparklesIcon className="h-5 w-5 text-purple-600" />
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            AI Confidence Preview
          </span>
          {loading && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
          )}
        </div>
        
        {confidencePreview && (
          <div className="flex items-center gap-2">
            {getConfidenceIcon(confidencePreview.overallConfidence)}
            <span className={`px-2 py-1 text-xs rounded-full border ${getConfidenceColor(confidencePreview.overallConfidence)}`}>
              {Math.round(confidencePreview.overallConfidence * 100)}% Confidence
            </span>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-2">
          {error}
        </div>
      )}

      {/* Confidence Preview Content */}
      {confidencePreview && (
        <div className="space-y-4">
          {/* Overall Confidence */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">Overall Confidence</h4>
              <span className="text-lg font-bold text-gray-900 dark:text-white">
                {Math.round(confidencePreview.overallConfidence * 100)}%
              </span>
            </div>
            
            {/* Confidence Bar */}
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
              <div 
                className="h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${confidencePreview.overallConfidence * 100}%`,
                  backgroundColor: confidencePreview.overallConfidence >= 0.9 ? '#10b981' :
                                   confidencePreview.overallConfidence >= 0.8 ? '#3b82f6' :
                                   confidencePreview.overallConfidence >= 0.7 ? '#f59e0b' : '#ef4444'
                }}
              />
            </div>
            
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Based on file type, size, and processing profile
            </p>
          </div>

          {/* Task-Specific Confidences */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Task Confidence</h4>
            <div className="space-y-3">
              {Object.entries(confidencePreview.taskConfidences).map(([task, confidence]) => (
                <div key={task} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ChartBarIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">
                      {task.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                      <div 
                        className="h-1.5 rounded-full"
                        style={{
                          width: `${confidence * 100}%`,
                          backgroundColor: confidence >= 0.9 ? '#10b981' :
                                           confidence >= 0.8 ? '#3b82f6' :
                                           confidence >= 0.7 ? '#f59e0b' : '#ef4444'
                        }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 w-8">
                      {Math.round(confidence * 100)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Processing Time */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ClockIcon className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Estimated Processing Time</span>
              </div>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {formatTime(confidencePreview.estimatedProcessingTime)}
              </span>
            </div>
          </div>

          {/* Recommendations */}
          {confidencePreview.recommendations.length > 0 && (
            <div className="bg-lex-accent-soft border border-lex-accent rounded-lg p-4">
              <div className="flex items-start gap-2 mb-2">
                <InformationCircleIcon className="h-4 w-4 text-lex-accent mt-0.5 flex-shrink-0" />
                <h4 className="text-sm font-medium text-lex-accent">Recommendations</h4>
              </div>
              <ul className="space-y-1">
                {confidencePreview.recommendations.map((recommendation, index) => (
                  <li key={index} className="text-xs text-lex-accent flex items-start gap-2">
                    <span className="text-lex-accent mt-1">•</span>
                    <span>{recommendation}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* No Preview Message */}
      {!confidencePreview && !loading && (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400">
          <SparklesIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No confidence preview available</p>
          <p className="text-xs">Select a file and processing profile to see AI confidence estimates</p>
        </div>
      )}
    </div>
  );
}; 